/* eslint-disable @atlaskit/design-system/use-primitives-text */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { cssMap, jsx } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import { type IconProp } from '@atlaskit/button/new';
import VideoClipIcon from '@atlaskit/icon-lab/core/video-clip';
import CommentIcon from '@atlaskit/icon/core/comment';
import PageIcon from '@atlaskit/icon/core/page';
import PenIcon from '@atlaskit/icon/core/pen';
import PullRequestIcon from '@atlaskit/icon/core/pull-request';
import TaskIcon from '@atlaskit/icon/core/task';
import { token } from '@atlaskit/tokens';

import { SectionName } from '../../../../../common/analytics/section-name';
import { useSectionReveal } from '../../../../../common/analytics/useSectionReveal';
import { useAtlassianUserContext } from '../../../../../hooks/use-user-context';
import type { OrgMetricsResponse } from '../../../../../utils/orgMetrics';
import { SpacingWrapper } from '../../../SpacingWrapper';

import { messages } from './messages';

const styles = cssMap({
	section: {
		maxWidth: '100%',
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.400'),
		paddingInline: token('space.400'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		paddingBlock: '120px',
		'@media (max-width: 30rem)': {
			paddingInline: token('space.0'),
		},
	},
	content: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.400'),
	},
	title: {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#B7B9BE',
		font: token('font.heading.large'),
		fontFamily: token('font.family.brand.heading'),
		fontWeight: token('font.weight.regular'),
		// eslint-disable-next-line  @atlaskit/design-system/use-tokens-typography
		lineHeight: 1.5,
		margin: 0,
		maxWidth: '850px',
	},
	row: {
		display: 'flex',
		gap: token('space.300'),
		'@media (max-width: 30rem)': {
			flexDirection: 'column',
		},
	},
	heroCard: {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#BF63F3',
		borderRadius: token('radius.xlarge'),
		paddingInline: token('space.400'),
		paddingBlock: token('space.400'),
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'space-between',
		gap: token('space.100'),
		flexBasis: '63.66%',
		'@media (max-width: 30rem)': {
			flexBasis: 'unset',
			height: '412px',
		},
	},
	heroCardStat: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.200'),
	},
	heroNumber: {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#101214',
		fontFamily: token('font.family.brand.body'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontSize: '160px',
		fontWeight: token('font.weight.regular'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: 1,
		margin: 0,
		'@media (max-width: 30rem)': {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
			fontSize: '80px',
		},
	},
	heroLabel: {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#101214',
		font: token('font.body.large'),
		fontFamily: token('font.family.brand.body'),
		margin: 0,
	},
	heroDescription: {
		font: token('font.body.large'),
		fontFamily: token('font.family.brand.body'),
		margin: 0,
		maxWidth: '340px',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#101214',
	},
	statGridContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.300'),
		flexBasis: '34.33%',
	},
	statGrid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(2, 1fr)',
		gap: token('space.200'),
	},
	statCard: {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#1F1F21',
		borderRadius: token('radius.xlarge'),
		paddingInline: token('space.300'),
		paddingBlock: token('space.300'),
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.200'),
	},
	statNumber: {
		fontFamily: token('font.family.brand.body'),
		fontWeight: token('font.weight.regular'),
		margin: 0,
		fontVariantNumeric: 'lining-nums tabular-nums',
		FontFeatureSettings: "'ss01' on, 'ss02' on, 'ss07' on, 'cv11' on",
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontSize: '1.5rem',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '1.33',
	},
	statLabelContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#7D818A',
	},
	statLabel: {
		font: token('font.body'),
		fontFamily: token('font.family.brand.body'),
		margin: 0,
	},
	insightCard: {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#1F1F21',
		borderRadius: token('radius.xlarge'),
		paddingTop: token('space.300'),
		paddingRight: token('space.300'),
		paddingBottom: token('space.300'),
		paddingLeft: token('space.300'),
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#B7B9BE',
		font: token('font.body.large'),
		fontFamily: token('font.family.brand.body'),
	},
});

type StatCardProps = {
	count: number;
	label: string;
	icon: IconProp;
};

function StatCard({ count, label, icon: Icon }: StatCardProps): JSX.Element {
	const { formatNumber } = useIntl();
	return (
		<div css={styles.statCard}>
			<p css={styles.statNumber}>{formatNumber(count)}</p>
			<div css={styles.statLabelContainer}>
				<Icon label={label} />
				<p css={styles.statLabel}>{label}</p>
			</div>
		</div>
	);
}

export function IntroductionSection({ metrics }: { metrics: OrgMetricsResponse }): JSX.Element {
	const { formatMessage, formatNumber } = useIntl();
	const { totalNodes } = metrics;
	const { identity } = useAtlassianUserContext();
	const sectionRef = useSectionReveal<HTMLElement>(SectionName.Introduction);

	return (
		<SpacingWrapper>
			<section ref={sectionRef} css={styles.section}>
				<div css={styles.content}>
					{/* eslint-disable-next-line @atlaskit/design-system/use-heading */}
					<h2 css={styles.title}>
						<FormattedMessage
							{...messages.title}
							values={{ br: <br />, username: identity?.name?.split(' ')[0] }}
						/>
					</h2>

					<div css={styles.row}>
						{/* Hero card — Total items */}
						<div css={styles.heroCard}>
							<div css={styles.heroCardStat}>
								{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
								<p css={styles.heroNumber}>
									{formatNumber(totalNodes ?? 0, {
										notation: 'compact',
										maximumFractionDigits: 2,
									})}
								</p>
								<p css={styles.heroLabel}>
									<FormattedMessage {...messages.totalItemsLabel} />
								</p>
							</div>
							<p css={styles.heroDescription}>
								<FormattedMessage {...messages.totalItemsDescription} />
							</p>
						</div>

						<div css={styles.statGridContainer}>
							{/* 6 stat cards */}
							<div css={styles.statGrid}>
								<StatCard
									count={metrics.pagesCreated30d ?? 0}
									label={formatMessage(messages.pagesLabel)}
									icon={PageIcon}
								/>
								<StatCard
									count={metrics.totalJiraIssues ?? 0}
									label={formatMessage(messages.workItemsLabel)}
									icon={TaskIcon}
								/>
								<StatCard
									count={metrics.loomsRecorded30d ?? 0}
									label={formatMessage(messages.videosLabel)}
									icon={VideoClipIcon}
								/>
								<StatCard
									count={1234}
									label={formatMessage(messages.designsLabel)}
									icon={PenIcon}
								/>
								<StatCard
									count={1234}
									label={formatMessage(messages.pullRequestsLabel)}
									icon={PullRequestIcon}
								/>
								<StatCard
									count={metrics.commentsCreated7d ?? 0}
									label={formatMessage(messages.commentsLabel)}
									icon={CommentIcon}
								/>
							</div>

							{/* Top percentile insight */}
							<div css={styles.insightCard}>
								<FormattedMessage
									{...messages.topPercentileInsight}
									values={{ percentile: '10' }}
								/>
							</div>
						</div>
					</div>
				</div>
			</section>
		</SpacingWrapper>
	);
}
